import React from 'react';
import Head from 'next/head';
import Image from 'next/legacy/image';
import {
  MEMBERSHIPS,
  PAID_MEMBERSHIP_LEVELS,
  WEBSITE_NAME
} from '../../lib/constants';
import Title from '../../components/title';
import { useAuth0User } from '@hooks/useAuth0User';
import {
  BodyRegular,
  BodyRegularBold,
  H1,
  H4
} from '@components/atoms/typography';
import Button from '@components/atoms/button';
import { hasSufficientPrivileges } from '@lib/utils';
import {
  generateWelcomeToolsLinks,
  welcomePageArticleLinks
} from '@lib/page-utils/Welcome';
import Icon from '@components/atoms/icon';
import useWindowSize from '@hooks/useWindowSize';

const Welcome = () => {
  const { user } = useAuth0User();
  const canAccessPro = hasSufficientPrivileges(
    user,
    PAID_MEMBERSHIP_LEVELS[MEMBERSHIPS.STANDARD_MONTHLY_ID].LEVEL
  );

  const canAccessUltra = hasSufficientPrivileges(
    user,
    PAID_MEMBERSHIP_LEVELS[MEMBERSHIPS.ULTRA_YEARLY_ID].LEVEL
  );

  const { width } = useWindowSize();
  const isMobile = width < 768;

  return (
    <div>
      <Head>
        <title>
          {'Welcome | '}
          {WEBSITE_NAME}
        </title>
      </Head>
      <Title>{'Welcome'}</Title>
      <div className="bg-white px-5 md:px-10">
        <div className="mx-auto max-w-[900px] py-12">
          <div className="space-y-3">
            <H1>Welcome to FFH! 🎉</H1>
            <BodyRegular>
              Thanks for being one of our subscribers. You're one step closer to
              winning your Fantasy Football Mini League.
            </BodyRegular>
          </div>
          <hr className="bg-neutral-300 h-[1px] my-4 md:my-8" />
          <div className="space-y-3">
            <H4 className="mb-4">Learn about our tools</H4>
            {generateWelcomeToolsLinks({ canAccessUltra, canAccessPro }).map(
              (
                {
                  header,
                  description,
                  buttonLink,
                  buttonText,
                  openInNewLink,
                  isLocked,
                  isLockedText
                },
                index
              ) => (
                <div
                  key={`${header}-${index}`}
                  className="p-5 flex flex-col md:flex-row md:justify-between items-start md:items-center bg-neutral-100 rounded-lg"
                >
                  <div className="flex flex-col md:basis-3/4 space-y-2">
                    <div className="flex items-center mb-1">
                      {isLocked && (
                        <Icon name="Lock" className="w-5 h-5 mr-1" />
                      )}
                      <BodyRegularBold>{header}</BodyRegularBold>
                      {isLocked && (
                        <BodyRegular
                          textColor=" bg-primary-600 text-neutral-50"
                          className="ml-2 rounded-lg py-1 px-2"
                        >
                          {isLockedText}
                        </BodyRegular>
                      )}
                    </div>
                    <BodyRegular>{description}</BodyRegular>
                  </div>
                  {buttonLink && (
                    <div className="md:basis-1/4 flex justify-end mt-5 md:mt-0">
                      <Button
                        href={buttonLink}
                        isLink={!openInNewLink}
                        target={openInNewLink ? '_blank' : '_self'}
                        variant="primary"
                        className="whitespace-nowrap"
                      >
                        {buttonText}
                      </Button>
                    </div>
                  )}
                </div>
              )
            )}
          </div>
          <H4 className="mt-8 mb-4">Read our latest articles</H4>
          <div className="flex flex-col md:flex-row space-x-3 space-y-5 md:space-y-0">
            {welcomePageArticleLinks.map(
              ({ header, description, buttonLink, buttonText, imageName }) => (
                <div
                  key={imageName}
                  className="bg-neutral-100 flex-1 rounded-lg"
                >
                  <Image
                    src={`/images/welcome-page/${imageName}`}
                    width={isMobile ? 500 : 292}
                    height={isMobile ? 275 : 165}
                    alt={`${header} image`}
                    className="rounded-t-lg"
                    objectFit="cover"
                  />
                  <div className="px-3 py-4">
                    <BodyRegularBold>{header}</BodyRegularBold>
                    <BodyRegular className="my-3">{description}</BodyRegular>
                    <Button href={buttonLink} isLink variant="primary">
                      {buttonText}
                    </Button>
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Welcome;
